import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  X, Calendar, Tag, Users, CheckSquare, MessageSquare,
  Clock, AlignLeft, ShieldAlert, CheckCircle2, AlertCircle } from
'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription } from
'@/shared/components/ui/sheet';
import { Button } from '@/shared/components/ui/button';
import { Textarea } from '@/shared/components/ui/textarea';
import { toast } from 'sonner';
import { cardApi } from '../services/eztaskApi';

export default function EzTaskDrawer({ cardId, isOpen, onClose, onUpdate }) {const { t } = useTranslation("eztask");
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approvalComment, setApprovalComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !cardId) return;

    const fetchCard = async () => {
      setLoading(true);
      try {
        const data = await cardApi.get(cardId);
        setCard(data);
      } catch (err) {
        toast.error('Failed to load task details');
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [cardId, isOpen]);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await cardApi.approve(cardId, approvalComment);
      toast.success('Task approved and resumed');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Approval failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[400px] sm:w-1/3 overflow-y-auto bg-background border-l">
        {loading ?
        <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
          </div> :
        card ?
        <div className="flex flex-col h-full">
            <SheetHeader className="px-0 pb-6 border-b">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-primary opacity-70">
                  {card.is_agent_task ? 'Agent Task (Jeff)' : 'Standard Task'}
                </span>
                {card.agent_status === 'PENDING_APPROVAL' &&
              <span className="text-[10px] bg-warning/20 text-warning px-2 py-0.5 rounded-full font-bold uppercase">{t("pendingApproval", "Pending Approval")}

              </span>
              }
              </div>
              <SheetTitle className="text-2xl font-bold leading-tight text-foreground">
                {card.title}
              </SheetTitle>
              <SheetDescription className="text-muted-foreground">{t("in", "In")}
              {card.list_name}{t("ref", "\u2022 Ref:")}{card.reference_id || 'N/A'}
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto py-6 space-y-8 custom-scrollbar">
              {/* HITL Approval Block */}
              {card.agent_status === 'PENDING_APPROVAL' &&
            <div className="bg-[hsl(var(--warning-light))] border border-[hsl(var(--warning)/.2)] rounded-xl p-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[hsl(var(--warning)/.1)] rounded-lg text-warning">
                      <ShieldAlert className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-warning">{t("approvalRequired", "Approval Required")}</h4>
                      <p className="text-sm text-muted-foreground">{t("thisHighvalueAgent", "This high-value agent action requires")}
                    {card.approval_role || 'Manager'}{t("signoff", "sign-off.")}
                  </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{t("approvalComment", "Approval Comment")}</label>
                    <Textarea
                  value={approvalComment}
                  onChange={(e) => setApprovalComment(e.target.value)}
                  placeholder="Add an optional comment..."
                  className="bg-background border-border focus:ring-[hsl(var(--warning)/.5)]" />
                
                  </div>

                  <div className="flex gap-3">
                    <Button
                  className="flex-1 bg-warning hover:opacity-90 text-white font-bold"
                  onClick={handleApprove}
                  disabled={submitting}>
                  
                      {submitting ? 'Processing...' : 'Approve & Release'}
                    </Button>
                    <Button variant="outline" className="flex-1 border-border bg-transparent hover:bg-muted" onClick={onClose}>{t("reject", "Reject")}

                </Button>
                  </div>
                </div>
            }

              {/* Status & Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{t("status", "Status")}</span>
                  <div className="flex items-center gap-2 text-sm font-semibold capitalize bg-muted/30 p-2 rounded-lg border border-border">
                     <Clock className="h-4 w-4 text-muted-foreground" />
                     {card.agent_status || 'Active'}
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{t("priority", "Priority")}</span>
                  <div className="flex items-center gap-2 text-sm font-semibold capitalize bg-muted/30 p-2 rounded-lg border border-border">
                     <AlertCircle className="h-4 w-4 text-destructive" />
                     {card.priority || 'Medium'}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <AlignLeft className="h-4 w-4" />
                  <span className="text-[10px] font-bold uppercase tracking-widest">{t("description", "Description")}</span>
                </div>
                <p className="text-sm text-foreground leading-relaxed bg-muted/10 p-4 rounded-xl border border-border">
                  {card.description || 'No description provided.'}
                </p>
              </div>

              {/* Agent Metadata */}
              {card.is_agent_task && card.agent_metadata &&
            <div className="space-y-3">
                   <div className="flex items-center gap-2 text-muted-foreground">
                    <ShieldAlert className="h-4 w-4 text-primary" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">{t("agentDiagnostics", "Agent Diagnostics")}</span>
                  </div>
                  <div className="bg-muted p-4 rounded-xl font-mono text-[11px] text-foreground border border-border mb-6">
                    {Object.entries(card.agent_metadata).map(([key, val]) =>
                <div key={key} className="flex justify-between border-b border-border/50 py-1 last:border-0">
                        <span className="opacity-60">{key}:</span>
                        <span className="truncate max-w-[200px]">{JSON.stringify(val)}</span>
                      </div>
                )}
                  </div>
                </div>
            }
            </div>

            <div className="py-6 border-t border-border mt-auto">
              <Button variant="outline" className="w-full border-border bg-transparent hover:bg-muted" onClick={onClose}>{t("closeDetails", "Close Details")}

            </Button>
            </div>
          </div> :
        null}
      </SheetContent>
    </Sheet>);

}